package com.arena_web.ui.sections.feed.mobile

import androidx.compose.runtime.*
import com.arena_web.data.dto.CreateUserPostCommentRequest
import com.arena_web.data.dto.UserPostCommentDto
import com.arena_web.data.dto.UserPostDto
import com.arena_web.domain.repositories.UserPostRepository
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.feed.DoubleTapLikeArea
import com.arena_web.ui.components.feed.PostImageCollage
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.icons.TintedGlyph
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input

// Section gradient + border mirror Figma 8282:171373 ("Comments Section").
private const val SECTION_BG =
    "linear-gradient(171deg, rgba(52, 119, 250, 0.018) 27%, rgba(2, 4, 11, 0.3) 140%)"
private const val SEND_BG = "#0061FF"
// Heart filter matches the red used elsewhere when liked.
private const val LIKED_HEART_FILTER =
    "brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(4997%) hue-rotate(344deg) brightness(95%) contrast(99%)"

/**
 * Mobile post-comments bottom sheet — Figma 8282:171340. The phone counterpart
 * to the desktop [com.arena_web.ui.components.overlay.PostCommentsModal], opened
 * from a post's comment icon. Reuses the same load → submit → like flow.
 *
 * Layout (top → bottom): the post image preview and its like/comment counters
 * scroll with the comment list inside the sheet body; a message-input bar stays
 * pinned at the bottom of the panel. The Figma's back button is intentionally
 * dropped — the sheet dismisses via the scrim/drag handle instead.
 *
 * Lives behind the sheet's `--scale: var(--mobile-scale)` wrapper, so the
 * desktop `s()` helpers render 1:1 at the 390-px mobile baseline.
 */
@Composable
fun MobilePostCommentsSheet(
    post: UserPostDto,
    repository: UserPostRepository,
    onDismiss: () -> Unit,
    onCommentsChanged: (newCount: Int) -> Unit = {},
    onLikeClick: () -> Unit = {},
    /** Instagram-style double-tap on the photo. Idempotent like (a second
     *  double-tap on an already-liked post must not unlike it). */
    onDoubleTapLike: () -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    var comments by remember(post.id) { mutableStateOf<List<UserPostCommentDto>>(emptyList()) }
    var totalComments by remember(post.id) { mutableStateOf(post.commentsCount.toLong()) }
    var draft by remember(post.id) { mutableStateOf("") }
    var sending by remember { mutableStateOf(false) }

    // Initial load; re-loads when the post id changes so reopening for another
    // post starts clean.
    LaunchedEffect(post.id) {
        repository.getComments(post.id, mapOf("page" to "1", "size" to "50")).onSuccess { page ->
            comments = page.items
            totalComments = page.totalItems
            onCommentsChanged(page.totalItems.toInt())
        }
    }

    val submit = {
        val text = draft.trim()
        if (text.isNotEmpty() && !sending) {
            sending = true
            scope.launch {
                repository.createComment(post.id, CreateUserPostCommentRequest(text)).onSuccess {
                    draft = ""
                    repository.getComments(post.id, mapOf("page" to "1", "size" to "50"))
                        .onSuccess { page ->
                            comments = page.items
                            totalComments = page.totalItems
                            onCommentsChanged(page.totalItems.toInt())
                        }
                }
                sending = false
            }
        }
    }

    val toggleLike: (UserPostCommentDto) -> Unit = { comment ->
        val wasLiked = comment.isLikedByMe
        val optimistic = comment.copy(
            isLikedByMe = !wasLiked,
            likesCount = (comment.likesCount + if (wasLiked) -1 else 1).coerceAtLeast(0),
        )
        comments = comments.map { if (it.id == comment.id) optimistic else it }
        scope.launch {
            repository.likeComment(comment.id).onFailure {
                comments = comments.map { if (it.id == comment.id) comment else it }
            }
        }
    }

    // Full-height sheet: the photo, counters and comments all scroll together in
    // one area; only the input bar stays pinned at the bottom.
    BottomSheet(onDismiss = onDismiss, contentGap = 16, fullHeight = true) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(12)
                .styleModifier {
                    property("flex", "1")
                    property("min-height", "0")
                    property("overflow-y", "auto")
                    property("scrollbar-width", "none")
                }
        ) {
            // Post image preview — 0.5px gradient hairline, 18-radius (Figma
            // 8282:171365). `flex-shrink: 0` keeps it at full natural size so the
            // scroll never squashes the photo; it scrolls away with the content.
            if (post.images.isNotEmpty()) {
                var viewerIndex by remember(post.id) { mutableStateOf<Int?>(null) }
                DoubleTapLikeArea(
                    onDoubleTapLike = onDoubleTapLike,
                    modifier = Modifier
                        .fillMaxWidth()
                        .sBorderRadius(18)
                        .styleModifier {
                            property("overflow", "hidden")
                            property("border", "0.5px solid var(--arena-border-top)")
                            property("box-sizing", "border-box")
                            property("flex-shrink", "0")
                        },
                    onImageTap = { idx -> viewerIndex = idx },
                ) {
                    PostImageCollage(
                        urls = post.images.map { it.imageUrl },
                        layout = post.layout,
                        singleFullWidth = true,
                    )
                }
                viewerIndex?.let { idx ->
                    com.arena_web.ui.components.widgets.PhotoLightbox(
                        urls = post.images.map { it.imageUrl },
                        initialIndex = idx,
                        onDismiss = { viewerIndex = null },
                    )
                }
            }

            // Like + comment counters (Figma 8282:171366).
            Row(
                modifier = Modifier.sGap(16).styleModifier { property("flex-shrink", "0") },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Row(
                    modifier = Modifier.sGap(8).cursor(Cursor.Pointer).onClick { onLikeClick() },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Image(
                        src = AppIcons.Heart,
                        modifier = Modifier.sSize(24).styleModifier {
                            if (post.isLikedByMe) property("filter", LIKED_HEART_FILTER)
                        }
                    )
                    CounterText(post.likesCount.toString())
                }
                Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
                    Icon(src = AppIcons.Chat, modifier = Modifier.sSize(21))
                    CounterText(totalComments.toInt().toString())
                }
            }

            // Comments Section card — gradient surface + faint border (Figma 8282:171373).
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sBorderRadius(22)
                    .styleModifier {
                        property("flex-shrink", "0")
                        property("box-sizing", "border-box")
                        property("border", "1px solid rgba(41, 52, 138, 0.2)")
                        property("background", SECTION_BG)
                        property("padding", "0 ${s(12)} ${s(18)} ${s(12)}")
                    }
            ) {
                if (comments.isEmpty()) {
                    CommentsEmptyState()
                } else {
                    comments.forEach { c ->
                        CommentRow(comment = c, onLikeClick = { toggleLike(c) })
                    }
                }
            }

            // Trailing spacer — a sized Div at scroll-end keeps a comfortable
            // bottom gap (padding-bottom on a flex scroll container is dropped
            // at scroll-end on Chrome/Safari).
            Box(modifier = Modifier.fillMaxWidth().sHeight(8))
        }

        // ── Pinned message input bar (Figma 8555:189478) ──
        MessageInputBar(
            value = draft,
            onValueChange = { draft = it },
            onSubmit = { submit() },
            sending = sending,
        )
    }
}

// Empty comments placeholder — chatbubbles icon + prompt to comment first.
@Composable
private fun CommentsEmptyState() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sPadding(v = 28, h = 0).sGap(10),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        TintedGlyph(src = AppIcons.EmptyChats, sizePx = 44)
        SpanText(t.feedNoCommentsTitle, modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2))
        SpanText(
            t.feedNoCommentsSubtitle,
            modifier = Modifier.bodyMRegular().color(Color("var(--arena-text-muted)")),
        )
    }
}

@Composable
private fun CounterText(text: String) {
    SpanText(
        text,
        modifier = Modifier
            .sFontSize(18)
            .fontWeight(AppTypography.Regular)
            .sLineHeight(23)
            .sLetterSpacing(0.5)
            .color(com.arena_web.ui.theme.ThemeVars.Text2)
    )
}

// Figma 8282:171376 — avatar (mini player card) + name · time + text + like row.
@Composable
private fun CommentRow(comment: UserPostCommentDto, onLikeClick: () -> Unit) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.fillMaxWidth().sGap(14).styleModifier { property("padding-top", s(22)) },
        verticalAlignment = Alignment.Top,
    ) {
        Box(modifier = Modifier.styleModifier { property("flex-shrink", "0") }) {
            if (comment.authorCardImageUrl != null) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = "football",
                    size = PlayerCardSize.Highlight,
                    cardFrameUrl = comment.authorCardImageUrl,
                    cardAssetsBasePath = comment.authorCardAssetsBasePath,
                    cardTextColor = comment.authorCardTextColor,
                    avatarUrl = comment.authorCardAvatarUrl ?: AppIcons.EmptyAvatar,
                    hideRating = true,
                    hideShadow = true,
                )
            } else {
                Box(
                    modifier = Modifier
                        .sSize(40)
                        .sBorderRadius(100)
                        .styleModifier {
                            property("overflow", "hidden")
                            property("background-color", "#1A2238")
                            if (comment.authorAvatarUrl != null) {
                                property("background-image", "url('${comment.authorAvatarUrl}')")
                                property("background-size", "cover")
                                property("background-position", "center")
                            }
                        }
                )
            }
        }
        Column(modifier = Modifier.flexGrow(1).minWidth(0.px).sGap(6)) {
            Column(modifier = Modifier.fillMaxWidth().sGap(4)) {
                Row(modifier = Modifier.sGap(6), verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        comment.authorName,
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(18)
                            .sLetterSpacing(0.032)
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                            .styleModifier { property("white-space", "nowrap") }
                    )
                    Box(
                        modifier = Modifier
                            .sSize(2)
                            .sBorderRadius(100)
                            .backgroundColor(AppColors.Text.Muted)
                    )
                    SpanText(
                        formatRelativeTime(comment.createdAt),
                        modifier = Modifier
                            .sFontSize(12)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(12)
                            .sLetterSpacing(0.012)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3)
                            .styleModifier { property("white-space", "nowrap") }
                    )
                }
                SpanText(
                    comment.content,
                    modifier = Modifier
                        .fillMaxWidth()
                        .sFontSize(14)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(22)
                        .sLetterSpacing(0.028)
                        .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                )
            }
            Row(
                modifier = Modifier.sGap(6).cursor(Cursor.Pointer).onClick { onLikeClick() },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Image(
                    src = AppIcons.Heart,
                    modifier = Modifier.sSize(14).styleModifier {
                        if (comment.isLikedByMe) property("filter", LIKED_HEART_FILTER)
                    }
                )
                SpanText(
                    when (comment.likesCount) {
                        0 -> t.feedLikeZero
                        1 -> t.feedLikeOne
                        else -> t.feedLikeMany(comment.likesCount)
                    },
                    modifier = Modifier
                        .sFontSize(14)
                        .fontWeight(AppTypography.Light)
                        .sLineHeight(18)
                        .sLetterSpacing(0.028)
                        .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                )
            }
        }
    }
}

@Composable
private fun MessageInputBar(
    value: String,
    onValueChange: (String) -> Unit,
    onSubmit: () -> Unit,
    sending: Boolean,
) {
    val t = LocalAppStrings.current
    // Figma 8555:189478 — var(--l-s2, #09142d) pill with a 0.5px white-40 hairline, attach
    // icon on the left, send button on the right.
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(48)
            .sGap(4)
            .styleModifier {
                property("background", "var(--l-s2, #09142d)")
                property("border", "0.5px solid var(--arena-border-top)")
                property("border-radius", s(28))
                property("padding", "0 ${s(12)}")
                property("box-sizing", "border-box")
                property("flex-shrink", "0")
                // Bottom offset so the pinned bar never sits flush against the
                // sheet's bottom edge (over the home-indicator safe area).
                property("margin-bottom", "calc(${s(12)} + env(safe-area-inset-bottom))")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            src = AppIcons.Attachment,
            modifier = Modifier.sSize(24).styleModifier { property("opacity", "0.7") }
        )
        Input(type = InputType.Text) {
            value(value)
            onInput { onValueChange(it.value) }
            attr("placeholder", t.feedMessagePlaceholder)
            onKeyDown { e ->
                if (e.key == "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    onSubmit()
                }
            }
            style {
                property("flex", "1 1 0")
                property("min-width", "0")
                property("background", "transparent")
                property("border", "none")
                property("outline", "none")
                property("color", "var(--arena-text-2)")
                property("font-size", s(16))
                property("letter-spacing", s(0.16))
                property("font-family", "Urbanist, sans-serif")
            }
        }
        Box(
            modifier = Modifier
                .sSize(32)
                .sBorderRadius(100)
                .backgroundColor(Color(SEND_BG))
                .cursor(if (sending || value.isBlank()) Cursor.NotAllowed else Cursor.Pointer)
                .styleModifier {
                    property("flex-shrink", "0")
                    if (sending || value.isBlank()) property("opacity", "0.5")
                }
                .onClick { onSubmit() },
            contentAlignment = Alignment.Center,
        ) {
            Icon(src = AppIcons.Send, modifier = Modifier.sSize(16))
        }
    }
}

/** "2 min ago" / "1 hour ago" / "1 day ago" / dated fallback. */
private fun formatRelativeTime(isoDate: String): String = try {
    val date = kotlin.js.Date(isoDate)
    val now = kotlin.js.Date()
    val diffMs = now.getTime() - date.getTime()
    val diffMin = (diffMs / 60000).toLong()
    val diffHours = (diffMs / 3600000).toLong()
    val diffDays = (diffMs / 86400000).toLong()
    when {
        diffMin < 1 -> "just now"
        diffMin < 60 -> "$diffMin min ago"
        diffHours < 24 -> "$diffHours hour${if (diffHours == 1L) "" else "s"} ago"
        diffDays < 7 -> "$diffDays day${if (diffDays == 1L) "" else "s"} ago"
        else -> {
            val day = date.getDate()
            val month = date.toLocaleString("en", js("({month:'short'})")).toString()
            "$day $month"
        }
    }
} catch (_: Throwable) {
    isoDate
}
